/**
 * BigQuery analytics event loggers for the single-order layout.
 *
 * Each exported function maps to one user-facing action (screen view,
 * tab click, modal open, button click) and delegates to a shared
 * {@link logSingleOrderLayoutEvent} helper that normalises common fields.
 *
 * All data is sourced from the Postgres-backed GeneralOrderDetails,
 * AuthOnFile, and SingleCoverage models — the legacy MedicalPaOrder
 * parameter has been removed.
 *
 * @module singleOrderLayoutEventLogger
 */
import {
  AuthOnFile,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { getAllJCodesFromAuthOnFile } from "./existingDocsToLs";

/**
 * Common fields shared by every event logger in this module.
 *
 * `order_id` and `patient_id` are required because they are written
 * verbatim to BigQuery by {@link logSingleOrderLayoutEvent}; callers must
 * supply them explicitly (the helper no longer derives them from
 * `generalOrderDetails`).
 */
interface BaseLogEventParams {
  /** Order id written to BigQuery's `order_id` column. */
  order_id: string;
  /** Patient id (or MRN) written to BigQuery's `patient_id` column. */
  patient_id: string;
  /** Organization id written to BigQuery's `org_id` column. */
  orgId: string | null | undefined;
  /** Authenticated user id. */
  userId: string | undefined;
  /** Authenticated user email. */
  userEmail: string | undefined;
  /** Primary payer name; surfaced inside `additional_data.payer_name`. */
  payerName: string;
  /** Optional auth-on-file slice; only required by loggers that read auth status / J-codes. */
  authOnFileDetails?: AuthOnFile | null | undefined;
  /** Optional general order details slice; required by loggers that read demographics or regimen. */
  generalOrderDetails?: GeneralOrderDetails | null | undefined;
  /** Optional coverage rows; required by loggers that read primary coverage info. */
  coverageDetails?: SingleCoverage[] | null | undefined;
}

/**
 * Extended params for the internal {@link logSingleOrderLayoutEvent} helper.
 * Adds the event name and optional extra payload on top of {@link BaseLogEventParams}.
 */
interface LogEventParams extends BaseLogEventParams {
  eventName: EventsName | string;
  additionalData?: Record<string, any>;
}

/**
 * Base helper that enqueues a single-order-layout event for BigQuery.
 *
 * Writes the caller-supplied `order_id`, `patient_id`, and `orgId` directly
 * to the worker queue (no fallback derivation from `generalOrderDetails` —
 * loggers must pass these fields explicitly). All errors are swallowed and
 * logged to the console because analytics must not block UX.
 *
 * @param params - Event payload assembled by an exported logger
 */
const logSingleOrderLayoutEvent = async ({
  eventName,
  order_id,
  patient_id,
  orgId,
  userId,
  userEmail,
  payerName,
  additionalData = {},
}: LogEventParams) => {
  try {
    await apiWorkerManager.enqueueAndProcess({
      patient_id: patient_id ?? "",
      order_id: order_id ?? "",
      org_id: orgId ?? "",
      event_name: eventName,
      user_id: userId ?? "",
      user_email: userEmail ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        payer_name: payerName,
        ...additionalData,
      },
    });
  } catch (error) {
    // Silently handle errors - logging is non-blocking
    console.error("Failed to log event to BigQuery:", error);
  }
};

/**
 * Logs when the auth status screen is first shown
 */
export const logAuthStatusScreenShown = async (params: BaseLogEventParams) => {
  const jCodes =
    getAllJCodesFromAuthOnFile(params.authOnFileDetails ?? undefined) ?? [];

  await logSingleOrderLayoutEvent({
    ...params,
    eventName: EventsName.AUTH_STATUS_SCREEN_SHOWN,
    additionalData: {
      screen_name: ScreenNamesForBigQuery.WORKLIST,
      source: ScreenNamesForBigQuery.WORKLIST,
      auth_status: params.authOnFileDetails?.auth_status ?? "",
      drug_name: params.generalOrderDetails?.payload?.regimen_name ?? "",
      j_codes: jCodes?.map((jCode) => jCode?.j_code ?? "").join(",") ?? "",
    },
  });
};

/**
 * Logs when a tab is selected in the auth status screen
 */
export const logAuthTabSelected = async (
  params: BaseLogEventParams & { tabLabel: string },
) => {
  await logSingleOrderLayoutEvent({
    ...params,
    eventName: EventsName.AUTH_TAB_SELECTED + " (" + params.tabLabel + ")",
    additionalData: {
      tab_selected: params.tabLabel,
    },
  });
};

/**
 * Logs when the mark as completed button is clicked
 */
export const logMarkAsCompletedButtonClicked = async (
  params: BaseLogEventParams & {
    masterAuthStatusOld: string | undefined;
    masterAuthStatusNew: string | undefined;
    planName: string;
    jCodes: string[];
  },
) => {
  await logSingleOrderLayoutEvent({
    ...params,
    eventName: EventsName.MARK_AS_COMPLETED_BUTTON_CLICKED,
    additionalData: {
      master_auth_status_old: params.masterAuthStatusOld ?? "",
      master_auth_status_new: params.masterAuthStatusNew ?? "",
      plan_name: params.planName,
      j_codes: params.jCodes.join(","),
    },
  });
};

/**
 * Logs when the happy flow modal is opened
 */
export const logHappyFlowModalOpened = async (params: BaseLogEventParams) => {
  await logSingleOrderLayoutEvent({
    ...params,
    eventName: EventsName.HAPPY_FLOW_MODAL_OPENED,
    additionalData: {
      modal_name: ModalNamesForBigQuery.HAPPY_FLOW_MODAL,
    },
  });
};

/**
 * Logs when the comment RPA modal is opened
 */
export const logCommentRpaModalOpened = async (params: BaseLogEventParams) => {
  await logSingleOrderLayoutEvent({
    ...params,
    eventName: EventsName.COMMENT_RPA_MODAL_NEW_OPENED,
    additionalData: {
      modal_name: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
    },
  });
};

/**
 * Logs when the start filing button is clicked
 */
export const logStartFilingClicked = async (params: BaseLogEventParams) => {
  const primaryCoverage = params.coverageDetails?.find(
    (coverage) => coverage.coverage_type === "primary",
  );
  await logSingleOrderLayoutEvent({
    ...params,
    eventName: EventsName.START_FILING_CLICKED,
    additionalData: {
      source: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
      patient_name: generateFullName(
        params.generalOrderDetails?.demographics?.first_name ?? "",
        "",
        params.generalOrderDetails?.demographics?.last_name ?? "",
      ),
      patient_mrn:
        params.generalOrderDetails?.demographics?.patient_id ??
        params.generalOrderDetails?.demographics?.patient_mrn ??
        "",
      coverage_type: primaryCoverage?.payer_name ?? "",
    },
  });
};

/**
 * Logs when the form selection modal is shown
 */
export const logFormSelectionModalShown = async (
  params: BaseLogEventParams,
) => {
  await logSingleOrderLayoutEvent({
    ...params,
    eventName: EventsName.MODAL_SHOWN,
    additionalData: {
      source: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
      modal_name: ModalNamesForBigQuery.FORM_SELECTION_MODAL,
    },
  });
};

/**
 * Logs when the view treatment plan button is clicked
 */
export const logViewTxPlanClicked = async (params: BaseLogEventParams) => {
  await logSingleOrderLayoutEvent({
    ...params,
    eventName: EventsName.VIEW_TX_PLAN_CLICKED,
  });
};
